import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { RefreshCw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { ColorDot, COLOR_LABELS, ACTIONABLE_COLORS } from './colorLabels';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PersistenciaGrupoControl = ({ token, instances }) => {
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotId, setSnapshotId] = useState('');
  const [instanceFilter, setInstanceFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const loadSnapshots = useCallback(async () => {
    const response = await fetch(`${API_URL}/api/persistencia/snapshots`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await response.json();
    setSnapshots(d.snapshots || []);
    setSnapshotId((prev) => prev || (d.snapshots?.[0]?.snapshot_id ?? ''));
  }, [token]);

  const loadSettings = useCallback(async () => {
    const response = await fetch(`${API_URL}/api/persistencia/settings`, { headers: { Authorization: `Bearer ${token}` } });
    setSettings(await response.json());
  }, [token]);

  const loadGrupoControl = useCallback(async () => {
    if (!snapshotId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ snapshot_id: snapshotId });
      if (instanceFilter) params.append('instance', instanceFilter);
      const response = await fetch(`${API_URL}/api/persistencia/grupo-control?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await response.json().catch(() => null);
      if (!response.ok) throw new Error(d?.detail || 'Error cargando grupo de control');
      setData(d);
    } catch (error) {
      toast.error(error.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, snapshotId, instanceFilter]);

  useEffect(() => { loadSnapshots(); loadSettings(); }, [loadSnapshots, loadSettings]);
  useEffect(() => { loadGrupoControl(); }, [loadGrupoControl]);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const response = await fetch(`${API_URL}/api/persistencia/settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ control_pct_default: parseFloat(settings.control_pct_default) })
      });
      if (!response.ok) throw new Error('Error guardando');
      toast.success('% de control actualizado (aplica desde el próximo snapshot)');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-gray-900">Grupo de Control</h3>
        <p className="text-sm text-gray-500">Por cada corrida semanal, un % de cada color queda como CONTROL (no recibe campaña), fijo hasta la próxima corrida.</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">% de Control (default)</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-2">
          {settings && (
            <>
              <Input
                type="number"
                min="0"
                max="100"
                className="w-24"
                value={settings.control_pct_default}
                onChange={(e) => setSettings({ ...settings, control_pct_default: e.target.value })}
                data-testid="control-pct-input"
              />
              <span className="text-sm text-gray-500">%</span>
              <Button size="sm" onClick={handleSaveSettings} disabled={savingSettings} data-testid="control-pct-save-btn">
                <Save className="h-4 w-4 mr-1" /> Guardar
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center gap-2">
        <select className="border rounded-md px-3 py-2 text-sm" value={snapshotId} onChange={(e) => setSnapshotId(e.target.value)} data-testid="grupo-control-snapshot-select">
          <option value="">Elegir snapshot...</option>
          {snapshots.map((s) => <option key={s.snapshot_id} value={s.snapshot_id}>{s.snapshot_date} {s.created_at ? new Date(s.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false }) : ''} ({s.total_students} alumnos)</option>)}
        </select>
        <select className="border rounded-md px-3 py-2 text-sm" value={instanceFilter} onChange={(e) => setInstanceFilter(e.target.value)} data-testid="grupo-control-instance-select">
          <option value="">Todas las instancias</option>
          {(instances || []).map((i) => <option key={i.slug} value={i.slug}>{i.name}</option>)}
        </select>
        <Button variant="outline" size="sm" onClick={loadGrupoControl} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {!snapshotId ? (
        <p className="text-sm text-gray-400" data-testid="grupo-control-no-snapshot">Todavía no hay ningún snapshot congelado. Congelá uno desde "Dashboard".</p>
      ) : data ? (
        <Card data-testid="grupo-control-result">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{data.total} alumnos en este snapshot</CardTitle>
            <CardDescription>{data.snapshot_date}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {ACTIONABLE_COLORS.map((c) => {
              const b = data.breakdown_por_color[c] || { accion: 0, control: 0 };
              const total = b.accion + b.control;
              return (
                <div key={c} className="flex items-center justify-between border-b py-2" data-testid={`grupo-control-row-${c}`}>
                  <div className="flex items-center gap-2">
                    <ColorDot color={c} />
                    <span className="text-sm">{COLOR_LABELS[c]}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span>Acción: <strong>{b.accion}</strong></span>
                    <span>Control: <strong>{b.control}</strong></span>
                    <span className="text-gray-400">{total > 0 ? `${Math.round((b.control / total) * 100)}%` : '-'} control</span>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

export default PersistenciaGrupoControl;
